import { Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { OrderDetail } from '@/lib/api/orders';
import { formatMoney } from "@/lib/money";
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { STORE_ROUTES } from '@/config/router';
import { getLineItemIncludedTax } from '@/app/orders/[id]/utils/included-tax';

interface OrderItemsCardProps {
  order: OrderDetail;
}

const getItemImageUrl = (
  item: OrderDetail['items'][number],
  summaries: OrderDetail['summaries'],
) =>
  item.orderLineItemVariant?.imageUrl ||
  summaries?.[0]?.items?.find(
    (summaryItem: any) => summaryItem.variantId === item.variantId,
  )?.variant?.imageUrl;

const hasProperties = (properties: Record<string, unknown>) =>
  Object.keys(properties).length > 0;

function ItemProperties({ properties }: { properties: Record<string, unknown> }) {
  const entries = Object.entries(properties);

  if (entries.length === 0) return null;

  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {entries.map(([key, value]) => (
        <span
          key={key}
          className="bg-muted text-muted-foreground inline-flex items-center rounded px-1.5 py-0.5 text-xs"
        >
          <span className="font-medium">{key}:</span>
          <span className="ml-1">{String(value)}</span>
        </span>
      ))}
    </div>
  );
}

export function OrderItemsCard({ order }: OrderItemsCardProps) {
  const { t } = useTranslation('orders');
  const { orgRoutes, orgSlug } = useOrgNavigate();
  const storeRoutes = STORE_ROUTES(orgSlug, order.storeId);
  const filteredItems = order.items.filter(
    (item) => item.currency === order.currency,
  );
  const includedTaxByItem = getLineItemIncludedTax(order.items, order.summaries);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('items')}</CardTitle>
        <CardDescription>
          {filteredItems.length} {filteredItems.length !== 1 ? t('items') : t('item')} in this order
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">{t('image')}</TableHead>
                <TableHead>{t('product')}</TableHead>
                <TableHead className="text-right">{t('quantity')}</TableHead>
                <TableHead className="text-right">{t('price')}</TableHead>
                <TableHead className="text-right">{t('discount')}</TableHead>
                <TableHead className="text-right">{t('total')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item) => {
                const quantity = item.quantity || 1;
                const imageUrl = getItemImageUrl(item, order.summaries);
                // Tax included in the price was taken out of `adjustedAmount`: put it back so it does not read as a discount.
                const adjustedAmount = item.adjustedAmount + (includedTaxByItem.get(item.id) ?? 0);
                const discountAmount = Math.max((item.amount || 0) - (adjustedAmount || 0), 0);

                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div className="bg-muted relative h-16 w-16 overflow-hidden rounded-md border">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.orderLineItemProduct?.name || 'Product'}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <Package className="text-muted-foreground h-8 w-8" />
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        {item.productId ? (
                          <Link
                            to={storeRoutes.PRODUCT_DETAILS(item.productId)}
                            className="font-medium hover:underline"
                          >
                            {item.orderLineItemProduct?.name || 'Product'}
                          </Link>
                        ) : (
                          <span className="font-medium">{item.orderLineItemProduct?.name || 'Product'}</span>
                        )}
                        {item.orderLineItemVariant?.name && (
                          <span className="text-muted-foreground text-sm">
                            {item.orderLineItemVariant.name}
                          </span>
                        )}
                        {item.sku && <span className="text-muted-foreground text-xs">SKU: {item.sku}</span>}
                        {hasProperties(item.properties) && (
                          <ItemProperties properties={item.properties} />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{quantity}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(item.unitAmount, item.currency || order.currency)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {discountAmount > 0 ? (
                        <span className="text-destructive">
                          -{formatMoney(discountAmount, item.currency || order.currency)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          {formatMoney(0, item.currency || order.currency)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {item.amount !== adjustedAmount && (
                        <span className="text-muted-foreground pr-2 line-through">
                          {formatMoney(item.amount, item.currency || order.currency)}
                        </span>
                      )}
                      <span>{formatMoney(adjustedAmount, item.currency || order.currency)}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
